import type { EventFacts } from './factsApi';
import { aggregateFacts, env, money, throughputOf, type FactsAggregate, type Metric } from './engine';

/**
 * La estadística de las comparaciones. Pura: recibe hechos y devuelve
 * cifras con su base, su margen y, entre dos grupos, un test que dice si
 * la diferencia se sostiene o es ruido.
 *
 * Todo lo que es una proporción (leads por contacto, canje, consentimiento…)
 * se compara con el test z de dos proporciones con varianza agrupada, al 95 %.
 * Lo que no es una proporción (coste por lead, capturas por bloque) se
 * compara como diferencia sin test: no hay un modelo honesto para darle un
 * valor p con lo que sabemos. Con bases menores de 50 la cifra es
 * orientativa y se dice.
 */

export interface Proportion {
  num: number;
  base: number;
}

export interface TwoPropResult {
  p1: number;
  p2: number;
  /** p2 − p1 en puntos porcentuales. */
  diff: number;
  z: number;
  pValue: number;
  significant: boolean;
  /** Alguna base < 50: orienta, no concluye. */
  directional: boolean;
  /** Intervalo al 95 % de la diferencia, en puntos. */
  ciLow: number;
  ciHigh: number;
}

/** Cola normal de dos lados (Abramowitz y Stegun 26.2.17). */
function twoSidedP(z: number): number {
  const az = Math.abs(z);
  const t = 1 / (1 + 0.2316419 * az);
  const d = 0.3989423 * Math.exp((-az * az) / 2);
  const tail = d * t * (1.330274 * Math.pow(t, 4) - 1.821256 * Math.pow(t, 3) + 1.781478 * t * t - 0.356538 * t + 0.319382);
  return Math.min(1, Math.max(0, 2 * tail));
}

const r1 = (n: number) => Math.round(n * 10) / 10;

/** Test z de dos proporciones (agrupado), con el intervalo de la diferencia. */
export function twoProp(a: Proportion, b: Proportion): TwoPropResult | null {
  if (!a.base || !b.base) return null;
  const p1 = a.num / a.base;
  const p2 = b.num / b.base;
  const pool = (a.num + b.num) / (a.base + b.base);
  const se = Math.sqrt(pool * (1 - pool) * (1 / a.base + 1 / b.base));
  const z = se === 0 ? 0 : (p2 - p1) / se;
  const p = se === 0 ? 1 : twoSidedP(z);
  const seDiff = Math.sqrt((p1 * (1 - p1)) / a.base + (p2 * (1 - p2)) / b.base);
  const diff = (p2 - p1) * 100;
  return {
    p1: r1(p1 * 100),
    p2: r1(p2 * 100),
    diff: r1(diff),
    z: Math.round(z * 100) / 100,
    pValue: Math.round(p * 1000) / 1000,
    significant: p < 0.05,
    directional: a.base < 50 || b.base < 50,
    ciLow: r1(diff - 1.96 * seDiff * 100),
    ciHigh: r1(diff + 1.96 * seDiff * 100),
  };
}

// ── Métricas comparables ─────────────────────────────────────────────────

export type CompareKind = 'proportion' | 'usd' | 'number';

export interface CompareMetricDef {
  key: CompareKey;
  kind: CompareKind;
  /** Para colorear la diferencia: más alto es mejor, salvo coste y sin destino. */
  higherIsBetter: boolean;
}

export type CompareKey =
  | 'leadRate'
  | 'contactRate'
  | 'completion'
  | 'redemption'
  | 'consent'
  | 'deliveredPerInteraction'
  | 'unaccountedRate'
  | 'costPerLead'
  | 'capturesPerBlock'
  | 'peakBlock';

export const COMPARE_METRICS: CompareMetricDef[] = [
  { key: 'leadRate', kind: 'proportion', higherIsBetter: true },
  { key: 'contactRate', kind: 'proportion', higherIsBetter: true },
  { key: 'completion', kind: 'proportion', higherIsBetter: true },
  { key: 'redemption', kind: 'proportion', higherIsBetter: true },
  { key: 'consent', kind: 'proportion', higherIsBetter: true },
  { key: 'deliveredPerInteraction', kind: 'proportion', higherIsBetter: true },
  { key: 'unaccountedRate', kind: 'proportion', higherIsBetter: false },
  { key: 'costPerLead', kind: 'usd', higherIsBetter: false },
  { key: 'capturesPerBlock', kind: 'number', higherIsBetter: true },
  { key: 'peakBlock', kind: 'number', higherIsBetter: true },
];

export interface CompareValue {
  metric: Metric;
  /** Sólo para proporciones: numerador y base del test. */
  prop?: Proportion;
}

export interface GroupStats {
  key: string;
  label: string;
  color: string;
  eventIds: number[];
  agg: FactsAggregate;
  values: Record<CompareKey, CompareValue>;
  /** Contadores para la fila de contexto. */
  counts: { events: number; interactions: number; contacts: number; leads: number; delivered: number; unaccounted: number; captures: number; blocks: number; peak: number };
}

/** Las cifras de un grupo de eventos, cada una con su sobre. */
export function groupStats(key: string, label: string, color: string, facts: readonly EventFacts[]): GroupStats {
  const agg = aggregateFacts(facts);
  let captures = 0;
  let blocks = 0;
  let peak = 0;
  for (const f of facts) {
    for (const s of throughputOf(f.throughput)) {
      captures += s.total;
      blocks += s.blocks.length;
      peak = Math.max(peak, s.peak);
    }
  }
  const f = agg.funnel;
  const L = agg.ledger;
  const prop = (num: number, base: number, needs: Parameters<typeof env>[2]): CompareValue => ({ metric: env(num, base, needs), prop: { num, base } });
  const values: Record<CompareKey, CompareValue> = {
    leadRate: prop(f.leads, f.contacts, 'contacts'),
    contactRate: prop(f.contacts, f.interactions, 'interactions'),
    completion: prop(f.responses, f.interactions, 'interactions'),
    redemption: prop(L.delivered, L.won, 'prizesWon'),
    consent: prop(f.marketingOptIns, f.leads, 'leads'),
    deliveredPerInteraction: prop(Math.min(L.delivered, f.interactions), f.interactions, 'interactions'),
    unaccountedRate: prop(L.unaccounted, L.consumed, 'consumedUnits'),
    costPerLead: {
      metric: money(L.distributedValue.value != null && f.leads ? L.distributedValue.value / f.leads : null, L.distributedValue.state === 'PENDING' ? 'distributedValue' : 'leads'),
    },
    capturesPerBlock: { metric: blocks ? { state: 'MEASURED', value: Math.round((captures / blocks) * 10) / 10, unit: 'count' } : { state: 'PENDING', value: null, unit: 'count', needs: 'captures' } },
    peakBlock: { metric: peak ? { state: 'MEASURED', value: peak, unit: 'count' } : { state: 'PENDING', value: null, unit: 'count', needs: 'captures' } },
  };
  return {
    key,
    label,
    color,
    eventIds: facts.map((x) => x.event.id),
    agg,
    values,
    counts: { events: facts.length, interactions: f.interactions, contacts: f.contacts, leads: f.leads, delivered: L.delivered, unaccounted: L.unaccounted, captures, blocks, peak },
  };
}

export interface Difference {
  /** Diferencia respecto a la referencia, en la unidad de la métrica (puntos si es proporción). */
  diff: number | null;
  /** Mejor, peor o igual, según la dirección de la métrica. */
  verdict: 'better' | 'worse' | 'same' | 'unknown';
  test: TwoPropResult | null;
}

/** La diferencia de un grupo frente a la referencia en una métrica. */
export function differenceOf(def: CompareMetricDef, baseline: GroupStats, other: GroupStats): Difference {
  const a = baseline.values[def.key];
  const b = other.values[def.key];
  if (def.kind === 'proportion' && a.prop && b.prop) {
    const test = twoProp(a.prop, b.prop);
    if (!test) return { diff: null, verdict: 'unknown', test: null };
    const better = def.higherIsBetter ? test.diff > 0 : test.diff < 0;
    return { diff: test.diff, verdict: !test.significant ? 'same' : better ? 'better' : 'worse', test };
  }
  if (a.metric.value == null || b.metric.value == null) return { diff: null, verdict: 'unknown', test: null };
  const diff = Math.round((b.metric.value - a.metric.value) * 100) / 100;
  const better = def.higherIsBetter ? diff > 0 : diff < 0;
  return { diff, verdict: diff === 0 ? 'same' : better ? 'better' : 'worse', test: null };
}

/** Colores de grupo, en orden fijo: el color sigue al grupo, no a su posición. */
export const GROUP_COLORS = [
  'var(--color-dash-olive)',
  'var(--color-dash-lime)',
  'var(--color-dash-amber)',
  'var(--color-dash-teal)',
  'var(--color-dash-purple)',
  'var(--color-dash-orange)',
] as const;

export const MAX_GROUPS = GROUP_COLORS.length;
